import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';

/**
 * Offline queue for detection results and annotations.
 *
 * Design choice: uses IndexedDB via the tiny `idb` wrapper so payloads
 * survive page reloads and can be synced when connectivity returns.
 * The queue is generic — callers specify an endpoint and payload.
 */

export interface QueuedRequest {
  id?: number;
  url: string;
  payload: unknown;
  createdAt: number;
  retries: number;
}

const DB_NAME = 'tag-plate-offline';
const STORE_NAME = 'pending-requests';
const DB_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private getDb(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          }
        },
      });
    }
    return this.dbPromise;
  }

  /** Enqueue a request for later sync. */
  async enqueue(url: string, payload: unknown): Promise<void> {
    const db = await this.getDb();
    await db.add(STORE_NAME, {
      url,
      payload,
      createdAt: Date.now(),
      retries: 0,
    } as QueuedRequest);
  }

  /** Get all pending requests. */
  async getAll(): Promise<QueuedRequest[]> {
    const db = await this.getDb();
    return db.getAll(STORE_NAME);
  }

  /** Remove a successfully synced request. */
  async remove(id: number): Promise<void> {
    const db = await this.getDb();
    await db.delete(STORE_NAME, id);
  }

  /** Increment retry count for a failed sync attempt. */
  async incrementRetries(id: number): Promise<void> {
    const db = await this.getDb();
    const item = await db.get(STORE_NAME, id);
    if (item) {
      item.retries++;
      await db.put(STORE_NAME, item);
    }
  }

  /** Number of pending items. */
  async count(): Promise<number> {
    const db = await this.getDb();
    return db.count(STORE_NAME);
  }
}
